'use client';

import BDImage from '@shared/components/elements/bd-image';
import BDButton from '@shared/components/elements/button';
import BDTitle, { BDSubtitle } from '@shared/components/elements/title';
import { FAQListItem, orderProcessFaqs } from '@shared/components/home/faq';
import { HSection } from '@shared/components/home/section';
import { Product } from '@shared/lib/shopify/types';
import { premiumDateInWeeks, premiumDeliveryDate } from '@shared/lib/utils/date-utils';
import { renderMoney } from '@shared/lib/utils/money';
import polishingImage from '@shared/public/images/get-to-know/diamond-polishing.jpg';
import diamondSettingImage from '@shared/public/images/get-to-know/diamond-setting.jpg';
import finalProductImage from '@shared/public/images/get-to-know/final-product.png';
import igiCertificationImage from '@shared/public/images/get-to-know/igi_certificate.png';
import machineImage from '@shared/public/images/get-to-know/machine.jpg';
import orderDiamondImage from '@shared/public/images/hero/work_on_phone.jpg';
import hairBoxImage from '@shared/public/images/kit/hair-box.png';
import kitImage from '@shared/public/images/kit/left-side.png';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';

export default function OrderProcess({ kitProduct }: { kitProduct: Product }) {
  const orderSteps = [
    {
      title: 'Pick your get started kit',
      date: 'Today',
      cost: `${renderMoney(kitProduct.variants[0]!.price)} due`,
      image: kitImage,
      description:
        'Choose your get started kit and service level to begin. The kit includes everything you need to collect hair and finalize your diamond order. No commitment to purchase a diamond yet.',
    },
    {
      title: 'Collect joint hair and return',
      date: 'Approx. 1 week',
      image: hairBoxImage,
      description:
        "Use the kit's tools and instructions to collect a small amount of hair (6g per carat). A prepaid label is included to return the sample.",
    },
    {
      title: 'Finalize your diamond and jewelry order',
      date: 'Couple of minutes',
      cost: `1/2 due, ${renderMoney(kitProduct.variants[0]!.price)} refunded`,
      image: orderDiamondImage,
      description:
        'Use the included ring sizer and sample stones to finalize your order. The kit fee will be credited toward your purchase.',
    },
    {
      title: 'Diamond Growth',
      date: 'Approx. 1 month',
      image: machineImage,
      description:
        'We extract and purify carbon from the hair, then grow your diamond using a high-pressure, high-temperature (HPHT) process.',
    },
    {
      title: 'Polishing, Cutting, Engraving',
      date: 'Approx. 3 weeks',
      image: polishingImage,
      description:
        'Once grown, your diamond is polished, cut to shape and size, and engraved with your custom message (if specified).',
    },
    {
      title: 'Certification and Control',
      date: 'Approx. 1 month',
      image: igiCertificationImage,
      description:
        'The diamond is certified by IGI and issued an authenticity certificate with a unique serial number.',
    },
    {
      title: 'Jewelry Making',
      date: 'Approx. 1 month',
      image: diamondSettingImage,
      description:
        'If selected, your diamond is set into a piece of fine jewelry, handcrafted in the USA for exceptional quality.',
    },
    {
      title: 'Finalize and Delivery',
      date: 'Couple of days',
      cost: '1/2 remaining due',
      image: finalProductImage,
      description:
        "Your Bonded Diamond™ is ready. Pay the final balance, and we'll express ship your personalized piece to you.",
    },
  ];

  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('opacity-0');
            entry.target.classList.add('animate-fadein');
          }
        });
      },
      { threshold: 0.3 }
    );

    const currentRefs = [...stepRefs.current];

    currentRefs.forEach((ref) => {
      if (ref) {
        observer.observe(ref);
      }
    });

    return () => {
      currentRefs.forEach((ref) => {
        if (ref) {
          observer.unobserve(ref);
        }
      });
    };
  }, [stepRefs]);

  return (
    <section
      id="order-process"
      className="flex flex-col items-center gap-8 py-12 lg:mx-6 xl:text-lg"
    >
      <HSection bgClassName="bg-bd-contrast md:pl-auto">
        <BDTitle>Our Ordering Process</BDTitle>
        <p className="px-2 text-center text-sm md:px-10 md:text-base lg:text-lg">
          Because every Bonded Diamond™ is individually made, it will take approximately 3 months
          to complete your Bonded Diamond™ order. If you have an important date in mind, please
          consider using our premium processing option, which could guarantee delivery in{' '}
          {premiumDateInWeeks} weeks before {premiumDeliveryDate.format('MM/DD/YYYY')}.
        </p>
        <div className="relative grid grid-cols-1 gap-8 gap-x-12 bg-transparent pl-4 pt-6 md:mx-10 md:pl-0 lg:mx-20 lg:pt-10">
          {orderSteps.map((step, index) => (
            <div
              key={index}
              ref={(el) => {
                stepRefs.current[index] = el;
              }}
              className="relative opacity-0"
            >
              {/* <h2 className="ml-8 text-sm capitalize text-slate-100 md:hidden">{step!.title}</h2> */}
              <div
                className={clsx(
                  'group ml-8 grid grid-cols-3 md:ml-0 md:grid-cols-2 md:gap-x-24',
                  index % 2 === 1 && 'md:text-right'
                )}
              >
                <div
                  className={clsx(
                    'md:aspect-16/9 row-start-1 my-auto aspect-square overflow-hidden',
                    index % 2 === 1 && 'col-start-3 md:col-start-2'
                  )}
                >
                  <BDImage
                    src={step!.image!}
                    alt={step!.title}
                    className="h-full w-full object-cover object-center"
                  />
                </div>
                <div
                  className={clsx(
                    'col-span-2 row-start-1 flex flex-col justify-center gap-y-1 sm:gap-y-2 md:col-span-1',
                    index % 2 === 1
                      ? 'mr-4 sm:mr-10 md:mr-0 md:items-end'
                      : 'ml-4 sm:ml-10 md:ml-0 md:items-start'
                  )}
                >
                  <h4 className="font-normal capitalize lg:text-lg">{step!.title}</h4>
                  <p className="text-xs font-light md:text-sm lg:text-base">{step!.date}</p>
                  <p className="text-xs font-light md:text-sm lg:text-base">{step!.description}</p>
                </div>
                {/* decoration */}
                {index !== 0 && (
                  <div
                    key="first-half-line"
                    className="bg-bd-dark absolute -left-1 -top-[1em] h-[50%] w-px -translate-x-1/2 md:left-1/2"
                  ></div>
                )}
                {index !== orderSteps.length - 1 && (
                  <div
                    key="second-half-line"
                    className="bg-bd-dark absolute -left-1 top-[calc(50%+1rem)] h-[50%] w-px -translate-x-1/2 md:left-1/2"
                  ></div>
                )}
                <div className="text-bd-dark border-bd-dark absolute -left-1 top-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border md:left-1/2 lg:text-lg">
                  {index + 1}
                </div>
              </div>
            </div>
          ))}
        </div>
      </HSection>
      <HSection className="w-full">
        <BDSubtitle>Frequently Asked Questions</BDSubtitle>
        <div className="flex w-full flex-col gap-4">
          {orderProcessFaqs().map((faq, index) => (
            <FAQListItem key={index} faq={faq} />
          ))}
        </div>
      </HSection>
      <div className="flex w-full flex-row flex-wrap justify-center gap-x-4 gap-y-2">
        <BDButton
          styleType="secondary"
          href="/shop/service"
          className="mx-4 w-full px-12 py-2 md:w-auto"
        >
          Get Started Kit
        </BDButton>
        <BDButton
          styleType="secondary"
          href="/shop/diamonds/bonded"
          className="mx-4 w-full px-12 py-2 md:w-auto"
        >
          Build Your Diamond
        </BDButton>
      </div>
    </section>
  );
}
